import type { SlideClassification } from "pwapi";

import {
  WIPE_AI,
  SHOW_MASK,
  WIPE_CASES,
  WIPE_CASE_DETAIL,
} from "../actions/constants";

// As seen in OpenAPI (does not yet include LP):
// export type LESION_TYPE = "ASL" | "BCC" | "MEL" | "SK" | "VV" | "INCONCLUSIVE";

// https://github.com/pathologywatch/viewer-backend/blob/master/packages/backend/apps/ai/models.py#L13

export const LESION_LINE_TYPES = [
  "ASL",
  "BCC",
  "MEL",
  "SK",
  "VV",
  "LP",
  "INC",
  "CYST",
];

export const LESION_LINE_NAMES: { [key: string]: string } = {
  ASL: "Atypical Squamous Lesion",
  BCC: "Basal Cell Carcinoma",
  MEL: "Melanocytic Lesion",
  CYST: "Cyst",
  SK: "Seborrheic Keratosis",
  VV: "Verruca Vulgaris",
  LP: "Lipoma",
  INC: "Inconclusive",
};

// "Unergonomic", needed since backend only provides diagnosis_name
export function lesionLineTypeByName(name: string): string {
  const matches = LESION_LINE_TYPES.filter(
    (t) => LESION_LINE_NAMES[t] === name
  );
  return matches && matches.length ? matches[0] : "";
}

export const LESION_LINE_TYPE_HIERARCHY: {
  [key: string]: { [key: string]: string };
} = {
  ASL: {
    SCC: "Invasive Squamous Cell Carcinoma",
    SCC_INSITU: "Squamous Cell Carcinoma In-Situ",
    AK: "Actinic Keratosis",
    HAK: "Hypertrophic Actinic Keratosis",
    AIP: "Atypical Intraepidermal Proliferation",
    ASP: "Atypical Squamous Proliferation",
    SCC_ALIS: "SCC, At Least In-Situ",
  },
  BCC: {
    NOD: "Nodular",
    INF: "Infiltrative",
    SUP: "Superficial",
  },
  MEL: {
    MEL_JUNC_NEV: "Benign Junctional Nevus",
    MEL_BENIGN_NEV: "Benign Compound Nevus",
    MEL_DERMAL_NEV: "Benign Dermal Nevus",

    ASL_MEL: "Atypical Melanocytic Lesion",
    NOS_MEL: "Melanocytic Lesion NOS",
    LG_MEL: "Mild Atypical Melanocytic Lesion",
    HG_MEL: "Severe Atypical Melanocytic Lesion",
  },
  CYST: {
    PIC: "Pilar Cyst",
    EIC: "Epidermal Inclusion Cyst",
  },
  SK: {},
  VV: {},
  LP: {},
  INC: {},
};

export const combineSubtypes = (
  subtypes: Subtype[],
  separator: string = "-"
) => {
  return subtypes
    .map((s) => s.diagnosis)
    .filter((d) => d)
    .join(separator);
};

export const subtypeOrSubtypesDiagnosis = (
  lesionLine: LesionLine & { subtype?: Subtype }
) => {
  if (lesionLine.hasOwnProperty("subtype") && lesionLine.subtype) {
    return lesionLine.subtype.diagnosis;
  } else if (lesionLine.hasOwnProperty("subtypes") && lesionLine.subtypes) {
    return combineSubtypes(lesionLine.subtypes);
  } else {
    return undefined;
  }
};

export const subtypeHasRoiPositions = (lesionLine: LesionLine) => {
  return lesionLine.subtypes?.some((s) => s.roi_positions?.length);
};

export function lesionLineSubtypeByName(
  typeName: string,
  subtypeName: string
): string {
  if (typeName && subtypeName) {
    const lesionLineType = lesionLineTypeByName(typeName);
    if (lesionLineType) {
      const subtypes: { [key: string]: string } =
        LESION_LINE_TYPE_HIERARCHY[lesionLineType];
      if (subtypes) {
        const matches = Object.entries(subtypes).find(
          (e) => e[1] === subtypeName
        );
        if (matches && matches.length) {
          return matches[0];
        }
      }
    }
  }
  return "";
}

export interface ROIPosition {
  center_point: number[];
  radius: number;
  score?: number;
}

export type LesionLine = Exclude<
  SlideClassification["lesion_lines"],
  undefined
>[0];
export type Subtype = Exclude<LesionLine["subtypes"], undefined>[0];

export interface AIState {
  // Should be considered now, "AI UI" reducer
  // No longer holds "server data"
  maskLesionLineType?: string;
}

export const initialState: AIState = {
  maskLesionLineType: undefined,
};

const reducer = (state: AIState = initialState, action: any): AIState => {
  switch (action.type) {
    case SHOW_MASK:
      return {
        ...state,
        maskLesionLineType: action.payload.maskLesionLineType,
      };
    case WIPE_AI:
    case WIPE_CASES:
    case WIPE_CASE_DETAIL:
      return initialState;
    default:
      return { ...state };
  }
};

export default reducer;
